import { FormattedMessage } from 'react-intl';
import { useState, useEffect, useCallback } from 'react';

import { Button } from 'ui-components/components/button';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import style from './style.module.less';
import WebHookForm from './webhook-form';
import WebHookList from './webhook-list';
import { Features } from '../../../../constants/features';
import { SellerSettings } from '../../../../enums/seller-settings/seller-settings';
import { useIsSellerFeatureEnabled } from '../../../../hooks/use-is-seller-feature-enabled';
import { getSellerSettingOptions } from '../../../../services/settings/seller-settings-service';
import { getAllWebhooks, addWebhook } from '../../api/integrations-api';
import messages from '../messages';
import sharedStyles from '../style.module.less';

const validateUrl = (url) => {
  // oxlint-disable-next-line unicorn/no-unsafe-regex
  return /^(https?|s?ftp):\/\/(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)|\/|\?)*)?$/i.test(
    url,
  );
};

const defaultHookType = 'ORDER_NOTIFY';

export const WebHooks = () => {
  const webHookDropdown = getSellerSettingOptions(SellerSettings.addToWebHook);

  const [addWebhookButton, setAddWebhookButton] = useState(false);
  const [name, setName] = useState();
  const [url, setUrl] = useState();
  const [selectedStore, setSelectedStore] = useState();
  const defaultWebhookType = webHookDropdown.find(({ value }) => value === defaultHookType);
  const [hookType, setHookType] = useState(defaultWebhookType);
  const [webhooks, setWebhooks] = useAsyncSafeState();
  const [isSaveClicked, setIsSavedClicked] = useState(false);
  const [currentEditedHookId, setCurrentEditedHookId] = useState();
  const isSellerFeatureApiV2Enabled = useIsSellerFeatureEnabled(Features.ShipStationApiV2);

  const apiV2webhookTypes = webHookDropdown.filter((option) => option.value && option.value.endsWith('_V2')).map((option) => option.value);

  // Filter out API V2 webhook types if the feature is not enabled
  const filteredWebhooksDropdown = !isSellerFeatureApiV2Enabled
    ? webHookDropdown.filter((webhook) => !apiV2webhookTypes.includes(webhook.value))
    : webHookDropdown;

  const fetchWebhooks = useCallback(async () => {
    const allWebhooks = await getAllWebhooks();
    setWebhooks(allWebhooks);
  }, [setWebhooks]);

  useEffect(() => {
    fetchWebhooks();
  }, []); // oxlint-disable-line

  const handleAddWebhookButtonClick = useCallback(() => {
    setName();
    setUrl();
    setSelectedStore();
    setHookType(defaultWebhookType);
    setAddWebhookButton(true);
  }, [setAddWebhookButton, defaultWebhookType]);

  const hasError = useCallback(() => {
    return !url?.length || !name?.length || !validateUrl(url);
  }, [url, name]);

  const onSave = useCallback(() => {
    setIsSavedClicked(true);

    if (hasError()) {
      return;
    }

    setAddWebhookButton(false);

    const data = {
      Name: name,
      HookType: hookType.value,
      Url: url,
      StoreGuid: selectedStore?.storeGuid,
    };

    const addNewWebhook = async () => {
      try {
        await addWebhook(data);
        fetchWebhooks();
      } catch (error) {
        throw new Error(`Error adding new webhook: ${error?.message}`);
      }
    };
    addNewWebhook();
    setIsSavedClicked(false);
  }, [fetchWebhooks, hasError, hookType, name, selectedStore, url]);

  const onCancel = () => {
    setAddWebhookButton(false);
    setCurrentEditedHookId(null);
    setName();
    setUrl();
    setSelectedStore();
    setHookType(defaultWebhookType);
  };

  return (
    <div className={sharedStyles['settings-section']}>
      <div className={sharedStyles['header-with-margin']}>
        <FormattedMessage {...messages.webhooks} />
      </div>
      <div className={sharedStyles.subtext}>
        <FormattedMessage
          {...messages.webhookText}
          values={{
            a: (chunks) => <a href='/settings/api'>{chunks}</a>,
          }}
        />
      </div>
      <div className={style['border-line']} />
      {webhooks?.length > 0 && (
        <WebHookList
          addWebhookButton={addWebhookButton}
          currentEditedHookId={currentEditedHookId}
          fetchWebhooks={fetchWebhooks}
          hookType={hookType}
          name={name}
          onCancel={onCancel}
          selectedStore={selectedStore}
          setCurrentEditedHookId={setCurrentEditedHookId}
          setHookType={setHookType}
          setName={setName}
          setSelectedStore={setSelectedStore}
          setUrl={setUrl}
          url={url}
          validateUrl={validateUrl}
          webHookDropdown={filteredWebhooksDropdown}
          webhooks={webhooks}
        />
      )}
      {addWebhookButton ? (
        <WebHookForm
          hookType={hookType}
          isSaveClicked={isSaveClicked}
          name={name}
          onCancel={onCancel}
          onSave={onSave}
          selectedStore={selectedStore}
          setHookType={setHookType}
          setName={setName}
          setSelectedStore={setSelectedStore}
          setUrl={setUrl}
          url={url}
          validateUrl={validateUrl}
          webHookDropdown={filteredWebhooksDropdown}
        />
      ) : null}
      {!addWebhookButton && !currentEditedHookId && (
        <div className={style['webhook-button']}>
          <Button onClick={handleAddWebhookButtonClick} theme='primary'>
            <FormattedMessage {...messages.addWebhook} />
          </Button>
        </div>
      )}
    </div>
  );
};
